import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const leCannet: ChauffeurPage = {
  slug: "le-cannet",
  title: "Chauffeur privé au Cannet – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC au Cannet : transferts aéroport de Nice, musée Bonnard, Vieux Cannet et liaisons Cannes, 7j/7.",
  keywords: [
    "chauffeur privé le cannet",
    "vtc le cannet",
    "chauffeur le cannet",
    "transfert le cannet aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC au Cannet",
    subtitle:
      "Transferts aéroport de Nice, liaisons avec Cannes, musée Bonnard et Vieux Cannet — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Le Cannet",
      "chauffeur Rocheville",
      "transfert aéroport Nice",
      "liaison Cannes",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC au Cannet",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur au Cannet",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 35 min par l’A8, suivi de vol et accueil personnalisé.",
      },
      {
        title: "Aux portes de Cannes",
        description:
          "Sur les hauteurs résidentielles au-dessus de Cannes : la Croisette et le Palais des Festivals en quelques minutes.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, à l’aise dans les quartiers résidentiels et les collines.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur au Cannet",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et les gares de Cannes.",
      },
      {
        title: "Liaisons avec Cannes",
        text: "Navettes vers la Croisette, le Palais des Festivals, les hôtels et le port, sans souci de stationnement.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour vos rendez-vous, visites et sorties.",
      },
      {
        title: "Culture & découverte",
        text: "Musée Bonnard, Vieux Cannet et Place Bellevue, avec dépose au plus près des ruelles piétonnes.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Le Cannet : Monaco, Saint-Tropez, Italie et stations de ski.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Musée Bonnard",
        description:
          "Seul musée dédié au peintre Pierre Bonnard, au cœur du Cannet.",
      },
      {
        name: "Vieux Cannet",
        description:
          "Village historique, ruelles piétonnes et façades peintes.",
      },
      {
        name: "Place Bellevue",
        description: "Belvédère avec vue panoramique sur la baie de Cannes.",
      },
      {
        name: "Chapelle Saint-Sauveur",
        description: "Chapelle décorée par l’artiste Théo Tobiasse.",
      },
      {
        name: "Rocheville",
        description: "Quartier résidentiel et commerçant du Cannet.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 35 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Le Cannet",
    subheading:
      "Des hauteurs résidentielles à la Croisette, un service premium sans stationnement.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé au Cannet et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 35 minutes), liaisons avec Cannes et trajets longue distance.",
      "Le Cannet s’étage sur les collines juste au-dessus de Cannes : nos chauffeurs bilingues connaissent les quartiers résidentiels, Rocheville et le Vieux Cannet, et vous relient à la Croisette en quelques minutes.",
      "Pour une visite du musée Bonnard, un panorama à la Place Bellevue ou une soirée à Cannes, votre chauffeur vous dépose au plus près et vous reprend à la demande, sans tourner pour se garer.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Liaisons rapides avec Cannes",
      "Dépose au plus près des ruelles",
    ],
  },
  process: {
    heading: "Réserver son chauffeur au Cannet",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Le Cannet", undefined, "au Cannet"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Le Cannet ?",
      a: "Comptez environ 35 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Assurez-vous les liaisons avec Cannes et la Croisette ?",
      a: "Oui, Le Cannet jouxte Cannes : nous rejoignons la Croisette, le Palais des Festivals et les hôtels en quelques minutes, sans souci de stationnement.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous au Cannet ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant les grands événements de Cannes ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Emmenez-vous les visiteurs au musée Bonnard et au Vieux Cannet ?",
      a: "Oui, avec dépose au plus près des ruelles piétonnes et reprise à la demande, pour le musée Bonnard, la Place Bellevue et le vieux village.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "VTC Côte d’Azur", href: "/chauffeur/cote-d-azur" },
    {
      label: "Location de voiture Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé au Cannet",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé au Cannet 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC au Cannet : transfert aéroport de Nice (~35 min), liaisons Cannes et Croisette, musée Bonnard, Vieux Cannet. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/le-cannet",
  },
};

export default leCannet;
